import {memo} from 'react';

import styled from 'styled-components';

import {attachModal} from '~/shared/components/ReduxModal';
import {getLocalizationService} from '~/shared/services/localisationService';
import {flexColumn} from '~/shared/theme/FlexLayout';
import {headingXS, headingS, body14Normal, body16Normal} from '~/shared/theme/typography';
import {media} from '~/shared/theme/media';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import PieButton from '~/shared/theme/pie/PieButton';

import illustrationImg from './assets/restaurant-not-found.png';

const Root = styled.div`
  ${flexColumn}
  align-items: center;
  padding: 0 16px 32px;

  &&& {
    width: 100%;
    background-color: ${({theme}) => theme.colors.surface};
  }

  ${media.minLargeMobile`
    padding: 0 32px 24px;
    &&& {
      width: 500px;
    }
  `}
`;

const Illustration = styled(ImageWithAlt).attrs({
  src: illustrationImg,
  noAlt: true,
})`
  width: 150px;
  height: 150px;
  margin-top: 8px;
  ${media.minLargeMobile`
    width: 200px;
    height: 200px;
    margin-top: 88px;
  `}
`;

const TextWrapper = styled.div`
  ${flexColumn}
  align-items: center;
  text-align: center;
  gap: 8px;
  margin-top: 24px;
  width: 100%;
  color: ${({theme}) => theme.colors.charcoal80};
`;

const Title = styled.h2`
  ${headingXS}
  margin: 0;
  ${media.minLargeMobile`
    ${headingS}
  `}
`;

const SubTitle = styled.p`
  ${body14Normal}
  line-height: 20px;
  margin: 0;
  ${media.minLargeMobile`
    ${body16Normal}
    line-height: 24px;
  `}
`;

const ActionButton = styled(PieButton)`
  width: 100%;
  margin-top: 32px;
`;

const RestaurantNotFoundModal = ({closeModal}: {closeModal: () => void}) => {
  const {t} = getLocalizationService();

  return (
    <Root>
      <Illustration />
      <TextWrapper>
        <Title id="modal-title">{t('restaurant_was_not_found')}</Title>
        <SubTitle>{t('restaurant_not_found_subtitle')}</SubTitle>
      </TextWrapper>
      <ActionButton variant="primaryLarge" onClick={closeModal}>{t('restaurant_not_found_action')}</ActionButton>
    </Root>
  );
};

type StaticTypes = {
  mobileBottomSheet?: boolean;
};

const EnhancedRestaurantNotFoundModal: React.FC<{closeModal: () => void}> & StaticTypes =
  memo(RestaurantNotFoundModal);

EnhancedRestaurantNotFoundModal.mobileBottomSheet = true;

export default attachModal('restaurantNotFoundModal', EnhancedRestaurantNotFoundModal);
